import { FC, useState } from "react";
import { Loader, LogInIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import axios from "axios";
import { useToast } from "../ui/use-toast";
import config from "@/config";
import { useNavigate } from "react-router-dom";

const AuthSchema = z.object({
  licenseCode: z.string().min(4, "License code is required"),
  email: z.string().email("Invalid email"),
  firstName: z
    .string()
    .min(2, "First name must be greater than 2 characters")
    .max(50, "First name must be less than 50 characters"),
  lastName: z
    .string()
    .max(50, "Last name must be less than 50 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  role: z.string() // Assuming role is a simple string for this example
});

// Define the schema for login
const LoginSchema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(6, "Password must be at least 6 characters")
});

const Demo: FC = () => {
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  type FormData = z.infer<typeof AuthSchema> & z.infer<typeof LoginSchema>;
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: zodResolver(isLoginMode ? LoginSchema : AuthSchema)
  });
  
  const switchMode = () => {
    setIsLoginMode(!isLoginMode);
    reset(); // Optionally reset form fields when switching mode
  };
  
  async function onSubmit(data: any) {
    setLoading(true);
    if (isLoginMode) {
      // Login logic
      try {
        const response = await axios.post(
          `${config.NEXT_PUBLIC_BASE_URL}/auth/login`,
          {
            email: data.email,
            password: data.password
          },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json"
            }
          }
        );
        
        if (response.data.accessToken) {
          localStorage.setItem("accessToken", response.data.accessToken);
          navigate("/dashboard");
        }
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        toast({
          title: "Login Failed",
          description: "Please check your credentials and try again."
        });
        setError(error?.response?.data?.message || "Login failed. Please check your credentials and try again.");
      }
    } else {
      // Registration logic
      try {
        const response = await axios.post(
          `${config.NEXT_PUBLIC_BASE_URL}/auth/register`,
          {
            ...data // Include all registration fields
          },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json"
            }
          }
        );
        
        if (response.status === 201) {
          // After successful registration, automatically log in the user
          try {
            const loginResponse = await axios.post(
              `${config.NEXT_PUBLIC_BASE_URL}/auth/login`,
              {
                email: data.email,
                password: data.password
              },
              {
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json"
                }
              }
            );
            if (loginResponse.data.accessToken) {
              localStorage.setItem("accessToken", loginResponse.data.accessToken);
              setLoading(false);
              return navigate("/dashboard");
            }
          } catch (loginError) {
            setLoading(false);
            toast({
              title: "Automatic Login Failed",
              description: "Please log in manually."
            });
            setError("Automatic Login Failed");
          }
        }
      } catch (error: any) {
        setLoading(false);
        toast({
          title: "Registration Failed",
          description: "Please try again later."
        });
        setError(
          (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length > 0)
            ? error.response.data.errors.join(", ")
            : (error?.response?.data?.message || "Registration failed. Please try again later.")
        );
      }
    }
  }
  
  return (
    <div className="col-span-12 lg:col-span-6 flex justify-stretch items-center fade-in-50">
      <div className="border-l-[#0048D6] border-l-[.5px] grow flex flex-col items-center gap-3 lg:gap-5">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-1 w-full px-4"
        >
          <h1 className="text-4xl text-black mb-5">
            {isLoginMode ? "Login" : "Beta Access"}
          </h1>
          
          {!isLoginMode && (
            <div className="flex flex-col gap-3">
              <label
                htmlFor="licenseCode"
                className="text-sm text-black font-semibold"
              >
                License Code
              </label>
              <input
                {...register("licenseCode")}
                type="text"
                id="licenseCode"
                className="px-3 py-3 lg:w-1/3 w-[200px] border-[1px] border-[#7b8aa7]"
              />
              {errors.licenseCode && (
                <span className="text-xs text-red-500 font-bold">
                  {errors.licenseCode.message}
                </span>
              )}
            </div>
          )}
          
          {!isLoginMode && (
            <div className="flex flex-col lg:flex-row lg:justify-stretch gap-6 w-full lg:w-2/3">
              <div className="flex flex-col gap-3 mt-5 grow">
                <label
                  htmlFor="firstName"
                  className="text-sm text-black font-semibold"
                >
                  First Name
                </label>
                <input
                  {...register("firstName")}
                  type="text"
                  id="firstName"
                  className="px-3 py-3 w-full border-[1px] border-[#7b8aa7]"
                />
                {errors.firstName && (
                  <span className="text-xs text-red-500 font-bold">
                  {errors.firstName.message}
                </span>
                )}
              </div>
              
              <div className="flex flex-col gap-3 mt-5 grow">
                <label
                  htmlFor="lastName"
                  className="text-sm text-black font-semibold"
                >
                  Last Name
                </label>
                <input
                  {...register("lastName")}
                  type="text"
                  id="lastName"
                  className="px-3 py-3 w-full border-[1px] border-[#7b8aa7]"
                />
                {errors.lastName && (
                  <span className="text-xs text-red-500 font-bold">
                  {errors.lastName.message}
                </span>
                )}
              </div>
            </div>
          )}
          
          <div className="flex flex-col lg:flex-row lg:justify-stretch gap-6 w-full lg:w-2/3">
            <div className="flex flex-col gap-3 mt-5 grow">
              <label
                htmlFor="email"
                className="text-sm text-black font-semibold"
              >
                Email
              </label>
              <input
                {...register("email")}
                type="email"
                id="email"
                className="px-3 py-3 w-full border-[1px] border-[#7b8aa7]"
              />
              {errors.email && (
                <span className="text-xs text-red-500 font-bold">
                  {errors.email.message}
                </span>
              )}
            </div>
            
            <div className="flex flex-col gap-3 mt-5 grow">
              <label
                htmlFor="password"
                className="text-sm text-black font-semibold"
              >
                Password
              </label>
              <input
                {...register("password")}
                type="password"
                id="password"
                className="px-3 py-3 w-full border-[1px] border-[#7b8aa7]"
              />
              {errors.password && (
                <span className="text-xs text-red-500 font-bold">
                  {errors.password.message}
                </span>
              )}
            </div>
          </div>
          
          {!isLoginMode && (
            <div className="flex flex-col justify-stretch mt-5 w-2/3">
              <label
                htmlFor="role"
                className="text-sm text-black font-semibold block mb-2"
              >
                Role
              </label>
              <select
                {...register("role")}
                id="role"
                className="grow h-12 rounded-none border-[#7b8aa7] w-full px-2"
              >
                <option value="">Select a role</option>
                <option value="investor">Investor</option>
                <option value="developer">Developer</option>
                <option value="customer">Customer</option>
              </select>
              {errors.role && (
                <span className="text-xs text-red-500 font-bold">
                  {errors.role.message}
                </span>
              )}
            </div>
          )}
          
          <div className="flex flex-col items-stretch w-2/3">
            <button
              type="submit"
              className="grow mt-5 h-14 flex items-center justify-center text-sm font-bold bg-[#1450C4] hover:bg-[#1450C4] rounded-none text-white"
            >
              {loading ? (
                <Loader className="mr-2 animate-spin w-5 h-6" />
              ) : (
                <LogInIcon size={20} className="mr-2" />
              )}
              {isLoginMode ? "Login" : "Access the BETA"}
            </button>
            {error && (
              <span className="mb-5 mt-2 text-sm text-red-500 font-bold">
                {error}
              </span>
            )}
          </div>
          
          <a
            onClick={switchMode}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            {isLoginMode ? "Need to register?" : "Already Have an Account?"}
          </a>
        </form>
      </div>
    </div>
  );
};

export default Demo;
